import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { exchangeMascot, exchangeVoucher } from '@/features/redeem/api/redeemService';
import { useLanguage } from '@shared/contexts/LanguageContext';

export default function MascotActionPopup({ isOpen, onClose, mascot, totalPoint, voucherItems, onSuccess }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showConfirm, setShowConfirm] = useState(false);
  const [exchangeMode, setExchangeMode] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [encouragement, setEncouragement] = useState('');

  if (!isOpen || !mascot) return null;

  const canExchangeWithVoucher = voucherItems && voucherItems.length > 0;
  const pointPoint = mascot.exchange_point ?? 200;
  const hasEnoughPoints = totalPoint >= pointPoint;

  const handleRedeem = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      let response;
      if (exchangeMode === 'voucher') {
        const user_voucher_id = voucherItems?.[0]?.user_voucher_id;
        response = await exchangeVoucher(user_voucher_id, mascot.reward_id);
      } else {
        response = await exchangeMascot(Number(mascot.reward_id));
      }
      if (response) {
        setShowConfirm(false);
        setShowSuccess(true);
        setEncouragement(response.encouragement ?? t('redeem.addedToBag'));
      }
    } catch (error) {
      console.error('Exchange failed:', error);
      alert(error?.message || t('redeem.redeemFailed'));
      setShowConfirm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowConfirm(false);
    setShowSuccess(false);
    setExchangeMode(null);
    onClose();
  };

  const handleSuccessConfirm = () => {
    setShowSuccess(false);
    if (onSuccess) onSuccess();
    else handleClose();
  };

  // Case 3: is_collected = true
  if (mascot.is_collected) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={handleClose}>
        <div className="w-96 p-6 bg-white rounded-[20px] shadow-xl inline-flex flex-col justify-start items-center gap-6 cursor-default" onClick={e => e.stopPropagation()}>
          <div className="justify-center text-[#171717] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">{t('redeem.alreadyOwned')}</div>
          <div
            className="self-stretch h-11 px-2 pt-0.5 bg-[#BC313D] rounded-[999px] flex justify-center items-center gap-1 cursor-pointer"
            onClick={() => navigate('/profile', { state: { activeTab: 1 } })}
          >
            <div className="pb-0.5 flex justify-center items-center">
              <div className="text-center justify-start text-white text-lg font-medium font-['Noto_Sans_TC'] leading-7">{t('redeem.viewTreasureBag')}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success Popup (handled here if we just finished exchanging)
  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 px-4" onClick={handleSuccessConfirm}>
        <div className="w-96 px-6 pt-14 pb-6 relative bg-white rounded-[20px] shadow-xl inline-flex flex-col justify-start items-center cursor-default" onClick={e => e.stopPropagation()}>
          <div className="justify-start text-[#171717] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">{t('redeem.redeemSuccess')}</div>
          <div className="justify-start text-[#9B887B] text-base font-normal font-['Noto_Sans_TC'] leading-6">{encouragement}</div>
          <div className="h-10 left-[151px] top-0 absolute inline-flex justify-center items-end gap-[5px]">
            <div className="w-20 h-20 relative bg-white rounded-[999px] shadow-[0px_5px_10px_0px_rgba(55,84,170,0.20)] overflow-hidden flex items-center justify-center">
              {mascot.reward_media ? (
                <img src={mascot.reward_media} alt="Success Mascot" className="w-full h-full object-contain bg-white" />
              ) : (
                <div className="w-20 h-20 left-0 top-0 absolute bg-teal-500" />
              )}
            </div>
          </div>
          <div
            className="mt-6 self-stretch h-11 px-2 pt-0.5 bg-[#BC313D] rounded-[999px] flex justify-center items-center gap-1 cursor-pointer"
            onClick={handleSuccessConfirm}
          >
            <div className="pb-0.5 flex justify-center items-center">
              <div className="text-center justify-start text-white text-lg font-medium font-['Noto_Sans_TC'] leading-7">{t('redeem.confirm')}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Confirm Popup
  if (showConfirm) {
    return (
      <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 px-4" onClick={() => setShowConfirm(false)}>
        <div className="w-96 p-6 bg-white rounded-[20px] shadow-xl inline-flex flex-col justify-start items-center gap-6 cursor-default" onClick={e => e.stopPropagation()}>
          <div className="self-stretch inline-flex justify-start items-center gap-8">
            <div className="justify-start">
              {exchangeMode === 'voucher' ? (
                <span className="text-[#171717] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">{t('redeem.useOneVoucher')}</span>
              ) : (
                <>
                  <span className="text-[#171717] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">使用 </span>
                  <span className="text-[#BC313D] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">{pointPoint}</span>
                  <span className="text-[#171717] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">{t('redeem.pointsForRedeem')}</span>
                </>
              )}
              <span className="text-[#BC313D] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">{mascot.category_y || '行'}｜{mascot.category_x || '金頂童子'}</span>
              <span className="text-[#171717] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">】</span>
            </div>
          </div>
          <div className="self-stretch inline-flex justify-start items-center gap-2">
            <div
              className="flex-1 h-11 px-2 pt-0.5 bg-[#BC313D]/10 rounded-[999px] outline outline-1 outline-offset-[-1px] outline-[#BC313D]/30 flex justify-center items-center gap-1 cursor-pointer"
              onClick={() => setShowConfirm(false)}
            >
              <div className="pb-0.5 flex justify-center items-center">
                <div className="text-center justify-start text-[#BC313D] text-base font-normal font-['SF_Pro'] leading-5">{t('redeem.cancel')}</div>
              </div>
            </div>
            <div
              className={`flex-1 h-11 px-2 pt-0.5 bg-[#BC313D] rounded-[999px] flex justify-center items-center gap-1 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={handleRedeem}
            >
              <div className="pb-0.5 flex justify-center items-center">
                <div className="text-center justify-start text-white text-lg font-medium font-['Noto_Sans_TC'] leading-7">
                  {isSubmitting ? t('redeem.redeeming') : t('redeem.redeem')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Case 1: is_collected = false, not enough points, no voucher
  if (!hasEnoughPoints && !canExchangeWithVoucher) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={handleClose}>
        <div className="w-96 p-6 bg-white rounded-[20px] shadow-xl inline-flex flex-col justify-start items-center gap-6 cursor-default" onClick={e => e.stopPropagation()}>
          <div className="justify-center text-[#171717] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">{t('redeem.insufficientForRedeem')}</div>
          <div
            className="self-stretch h-11 px-2 pt-0.5 bg-[#BC313D] rounded-[999px] flex justify-center items-center gap-1 cursor-pointer"
            onClick={handleClose}
          >
            <div className="pb-0.5 flex justify-center items-center">
              <div className="text-center justify-start text-white text-lg font-medium font-['Noto_Sans_TC'] leading-7">{t('redeem.confirm')}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Case 2: is_collected = false, has enough points OR has voucher -> choose payment method
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={handleClose}>
      <div className="w-96 p-6 bg-white rounded-[20px] shadow-xl inline-flex flex-col justify-start items-center gap-6 cursor-default" onClick={e => e.stopPropagation()}>
        <div className="justify-center text-[#171717] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">{t('redeem.selectRedeemMethod')}</div>
        
        <div className="self-stretch inline-flex justify-start items-center gap-2 w-full">
          {canExchangeWithVoucher ? (
            <div
              className="flex-1 h-11 px-2 pt-0.5 bg-[#BC313D]/10 rounded-[999px] outline outline-1 outline-offset-[-1px] outline-[#BC313D]/30 flex justify-center items-center gap-1 cursor-pointer"
              onClick={() => { setExchangeMode('voucher'); setShowConfirm(true); }}
            >
              <div className="pb-0.5 flex justify-center items-center">
                <div className="text-center justify-start text-[#BC313D] text-base font-normal font-['SF_Pro'] leading-5">{t('redeem.useVoucher')}</div>
              </div>
            </div>
          ) : (
            <div data-type="Storke" className="flex-1 self-stretch px-2 pt-0.5 bg-[#171717]/10 rounded-[999px] inline-flex justify-center items-center gap-1">
              <div className="pb-0.5 flex justify-center items-center">
                <div className="text-center justify-start text-[#6E625B]/75 text-base font-normal font-['SF_Pro'] leading-5">{t('redeem.insufficientVoucher')}</div>
              </div>
            </div>
          )}
          {!hasEnoughPoints ? (
            <div data-type="Storke" className="flex-1 self-stretch px-2 pt-0.5 bg-[#171717]/10 rounded-[999px] inline-flex justify-center items-center gap-1">
              <div className="pb-0.5 flex justify-center items-center">
                <div className="text-center justify-start text-[#6E625B]/75 text-base font-normal font-['SF_Pro'] leading-5">{t('redeem.insufficientPoints')}</div>
              </div>
            </div>
          ) : (
            <div
              className="flex-1 self-stretch px-2 pt-0.5 bg-[#BC313D] rounded-[999px] flex justify-center items-center gap-1 cursor-pointer"
              onClick={() => { setExchangeMode('point'); setShowConfirm(true); }}
            >
              <div className="pb-0.5 flex justify-center items-center">
                <div className="text-center justify-start text-white text-lg font-medium font-['Noto_Sans_TC'] leading-7">{t('redeem.redeemWithPoints')}</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Cancel button */}
        <div
          className="self-stretch h-11 px-2 pt-0.5 mt-2 bg-transparent rounded-[999px] outline outline-1 outline-offset-[-1px] outline-[#6E625B]/30 flex justify-center items-center gap-1 cursor-pointer"
          onClick={handleClose}
        >
          <div className="pb-0.5 flex justify-center items-center">
            <div className="text-center justify-start text-[#6E625B] text-lg font-medium font-['Noto_Sans_TC'] leading-7">{t('redeem.cancel')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
